import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { formatCurrency as fmtCurrency } from '../lib/format'
import type { Project, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Drawer } from '../components/ui/Drawer'
import { useClientOptions } from '../hooks/useEntityOptions'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface ProjectWithClient extends Project {
  client_name: string
  client_slug: string
}

export default function Projects() {
  const [projects, setProjects] = useState<ProjectWithClient[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [formData, setFormData] = useState({ name: '', client_slug: '', site_address: '' })
  const [saving, setSaving] = useState(false)
  const { options: clientOptions } = useClientOptions()

  const loadProjects = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.get<PaginatedResult<ProjectWithClient>>(
        `/projects?page=${page}&limit=20${search ? `&search=${encodeURIComponent(search)}` : ''}`
      )
      setProjects(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, search])

  useEffect(() => { loadProjects() }, [loadProjects])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/projects', formData)
      setDrawerOpen(false)
      setFormData({ name: '', client_slug: '', site_address: '' })
      loadProjects()
    } catch { /* error */ }
    finally { setSaving(false) }
  }

  function formatCurrency(n: number) {
    return fmtCurrency(n)
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title">Projects</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New Project</Button>
      </div>

      <div className="mb-4">
        <input
          type="search"
          placeholder="Search projects..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="input max-w-sm"
        />
      </div>

      {loading ? (
        <TableSkeleton />
      ) : projects.length === 0 ? (
        <EmptyState
          title="Ready to kick off a project?"
          description="Create your first project to start tracking work."
          action={<Button onClick={() => setDrawerOpen(true)}>New Project</Button>}
        />
      ) : (
        <>
          <div className="hidden sm:block table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Budget</th>
                  <th>Actual</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link
                        to={`/projects/${p.slug}`}
                        style={{ color: 'var(--accent-text)', fontSize: 14, fontWeight: 500 }}
                        className="hover:underline"
                      >
                        {p.name}
                      </Link>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{p.client_name}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{formatCurrency(p.budget_total)}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{formatCurrency(p.actual_cost_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.slug}`} className="mobile-card block">
                <div className="mobile-card-row">
                  <p className="truncate" style={{ fontSize: 14, fontWeight: 500, color: 'var(--accent-text)' }}>{p.name}</p>
                  <StatusBadge status={p.status} />
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{p.client_name}</p>
                <div className="mobile-card-row mt-2">
                  <div>
                    <span className="mobile-card-label">Budget</span>
                    <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{formatCurrency(p.budget_total)}</p>
                  </div>
                  <div className="text-right">
                    <span className="mobile-card-label">Actual</span>
                    <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{formatCurrency(p.actual_cost_total)}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New Project">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Project Name *</label>
            <input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>
          <div>
            <label className="label">Client *</label>
            <select
              value={formData.client_slug}
              onChange={(e) => setFormData({ ...formData, client_slug: e.target.value })}
              className="input"
              required
            >
              <option value="">Select client...</option>
              {clientOptions.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Site Address</label>
            <input
              value={formData.site_address}
              onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
              className="input"
            />
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%' }}>
            Create Project
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
